import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { useRealtimeTick } from "@/lib/realtime";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ChevronRight, FolderTree } from "lucide-react";
import { CATALOG_ICONS, CatalogIcon } from "@/lib/catalogIcons";

const emptyForm = { name: "", parent_id: null, icon: "Folder", color: "#3b82f6", order: 0 };

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#06b6d4", "#84cc16"];

function flattenTree(items, expanded) {
  const rows = [];
  const stack = items.filter((c) => !c.parent_id).reverse().map((n) => ({ node: n, depth: 0 }));
  while (stack.length) {
    const { node, depth } = stack.pop();
    const kids = items.filter((c) => c.parent_id === node.id);
    rows.push({ node, depth, kids });
    if (expanded.has(node.id)) {
      for (let i = kids.length - 1; i >= 0; i--) stack.push({ node: kids[i], depth: depth + 1 });
    }
  }
  return rows;
}

function TreeRow({ node, kids, depth, isOpen, toggle, openNew, openEdit, remove }) {
  return (
    <div data-testid={`catalog-node-${node.id}`}>
      <div
        className="group flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-slate-800/60 transition"
        style={{ paddingLeft: `${12 + depth * 20}px` }}
      >
        <button
          type="button"
          onClick={() => kids.length && toggle(node.id)}
          className={`h-5 w-5 flex items-center justify-center text-slate-500 transition ${isOpen ? "rotate-90" : ""} ${kids.length ? "" : "opacity-0"}`}
        >
          <ChevronRight size={14} />
        </button>
        <div
          className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: node.color + "22", border: "1px solid " + node.color + "55", color: node.color }}
        >
          <CatalogIcon name={node.icon} size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{node.name}</div>
          <div className="text-[10px] font-mono text-slate-500">{kids.length} alt klasör</div>
        </div>
        <div className="flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition">
          <Button size="sm" variant="ghost" onClick={() => openNew(node.id)} data-testid={`catalog-add-child-${node.id}`}>
            <Plus size={14} />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => openEdit(node)} data-testid={`catalog-edit-${node.id}`}>
            <Pencil size={14} />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => remove(node)} data-testid={`catalog-delete-${node.id}`}>
            <Trash2 size={14} className="text-red-400" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Catalogs() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [expanded, setExpanded] = useState(new Set());

  const load = () => http.get("/catalogs").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const tick = useRealtimeTick((m) => m.path.startsWith("/api/catalogs"));
  useEffect(() => { if (tick) load(); }, [tick]); // eslint-disable-line

  const roots = items.filter((c) => !c.parent_id);

  const toggle = (id) => {
    const s = new Set(expanded);
    if (s.has(id)) s.delete(id); else s.add(id);
    setExpanded(s);
  };

  const openNew = (parent_id = null) => {
    setEditing(null);
    setForm({ ...emptyForm, parent_id });
    setOpen(true);
  };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Katalog adı zorunlu"); return; }
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing) await http.put("/catalogs/" + editing.id, payload);
      else await http.post("/catalogs", payload);
      toast.success("Kaydedildi");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`"${c.name}" ve tüm alt klasörleri silinsin mi?`)) return;
    try {
      const { data } = await http.delete("/catalogs/" + c.id);
      toast.success(data.deleted + " katalog silindi");
      load();
    } catch { toast.error("Silinemedi"); }
  };

  const descendants = useMemo(() => {
    if (!editing) return new Set();
    const s = new Set([editing.id]);
    let stack = [editing.id];
    while (stack.length) {
      const cur = stack.pop();
      for (const c of items) if (c.parent_id === cur) { s.add(c.id); stack.push(c.id); }
    }
    return s;
  }, [editing, items]);

  const parentOptions = items.filter((c) => !descendants.has(c.id));

  return (
    <div className="space-y-6">
      <header className="animate-fade-up flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <FolderTree className="text-blue-400" /> Kataloglar
          </h1>
          <p className="text-slate-400 text-sm mt-1">Ürün kataloglarını ve iç içe klasörlerini yönetin</p>
        </div>
        <Button onClick={() => openNew(null)} className="bg-blue-600 hover:bg-blue-500" data-testid="catalog-new-root">
          <Plus size={16} className="mr-1" /> Yeni Kök Katalog
        </Button>
      </header>

      <Card className="border-slate-800 bg-slate-900/60 glass animate-fade-up delay-100">
        <CardContent className="p-3">
          {roots.length === 0 && (
            <div className="text-center text-slate-500 py-10">
              <FolderTree className="mx-auto mb-3 opacity-50" size={40} />
              <p>Henüz katalog yok. İlk kataloğu oluşturmak için "Yeni Kök Katalog" butonuna basın.</p>
            </div>
          )}
          <div className="space-y-0.5">
            {flattenTree(items, expanded).map(({ node, depth, kids }) => (
              <TreeRow key={node.id} node={node} kids={kids} depth={depth} isOpen={expanded.has(node.id)}
                       toggle={toggle} openNew={openNew} openEdit={openEdit} remove={remove} />
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Kataloğu Düzenle" : "Yeni Katalog"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Katalog Adı</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="bg-slate-950/50 border-slate-700" data-testid="catalog-form-name" placeholder="Örn: Mobil, DSL, Tivibu..." />
            </div>
            <div>
              <Label>Üst Katalog (opsiyonel)</Label>
              <select
                value={form.parent_id || ""}
                onChange={(e) => setForm({ ...form, parent_id: e.target.value || null })}
                className="w-full h-10 bg-slate-950/50 border border-slate-700 rounded-md px-3 text-sm"
                data-testid="catalog-form-parent"
              >
                <option value="">— Kök katalog —</option>
                {parentOptions.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>İkon</Label>
              <div className="grid grid-cols-8 gap-1.5 max-h-40 overflow-y-auto p-2 bg-slate-950/50 border border-slate-700 rounded-md">
                {CATALOG_ICONS.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setForm({ ...form, icon: name })}
                    data-testid={"catalog-icon-" + name}
                    className={"h-9 flex items-center justify-center rounded-md transition " + (form.icon === name ? "bg-blue-500/30 ring-1 ring-blue-400" : "hover:bg-slate-800")}
                    title={name}
                  >
                    <CatalogIcon name={name} size={16} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Renk</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    data-testid={"catalog-color-" + c}
                    className={"h-8 w-8 rounded-full transition " + (form.color === c ? "ring-2 ring-white ring-offset-2 ring-offset-slate-900" : "")}
                    style={{ background: c }}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label>Sıralama</Label>
              <Input type="number" value={form.order}
                     onChange={(e) => setForm({ ...form, order: e.target.value })}
                     className="bg-slate-950/50 border-slate-700" data-testid="catalog-form-order" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-700">İptal</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-500" data-testid="catalog-form-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
